import React, { useEffect, useState } from "react";
import { History, Trash2, User } from "lucide-react";
import api from "../lib/api";
import { usePaged, Paginator } from "../components/Paginator";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const TYPE_LABELS = {
  course: "Course", lesson: "Lesson", session: "Session", cohort: "Cohort",
  enrollment: "Learner enrolment", member: "Member", team: "Team", task: "Task",
  event: "Calendar event", service: "Service", document: "Document",
  "collab-doc": "Shared document", "prayer-request": "Prayer request",
  gallery: "Gallery photo", sermon: "Sermon", feedback: "Assignment feedback",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" }) + ", " + d.toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit" });
}

export default function DeletionLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/deletion-log").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const { page, setPage, pageCount, pageItems } = usePaged(rows, 25);

  return (
    <div className="space-y-6" data-testid="deletion-log-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold flex items-center gap-3" style={{ color: "var(--sj-text)" }}>
          <History className="h-9 w-9" style={{ color: "var(--sj-primary)" }} /> Deletion Log
        </h1>
        <p className="text-base mt-2" style={{ color: "var(--sj-text-2)" }}>An audit trail of who deleted what, and when. The most recent removals are shown first.</p>
      </div>

      <div className={`${CARD} overflow-hidden`}>
        {loading ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center" data-testid="deletion-log-empty">
            <Trash2 className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--sj-text-3)" }} />
            <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>No deletions recorded yet.</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--sj-border)" }}>
            {pageItems.map((r) => (
              <div key={r.id} data-testid={`deletion-row-${r.id}`} className="flex items-start gap-3 px-5 py-4">
                <span className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FDE7E7" }}>
                  <Trash2 className="h-4 w-4" style={{ color: "#B91C1C" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>{TYPE_LABELS[r.entity_type] || r.entity_type}</span>
                    <span className="font-medium text-sm truncate" style={{ color: "var(--sj-text)" }}>{r.label || "(unnamed)"}</span>
                  </div>
                  {r.reason ? <p className="text-xs mt-1" style={{ color: "var(--sj-text-2)" }}>“{r.reason}”</p> : null}
                  <div className="flex items-center gap-1.5 text-xs mt-1" style={{ color: "var(--sj-text-3)" }}>
                    <User className="h-3.5 w-3.5" /> {r.actor_name} · {timeAgo(r.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Paginator page={page} pageCount={pageCount} onPage={setPage} testid="deletion-log-paginator" />
    </div>
  );
}
